import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';

import Text from 'components/Text';
import { Column, Row } from 'components/Layout/Grid';
import TabNavigation from 'components/TabNavigation';
import OrderDropdown from 'components/OrderDropdown';
import {
  ordersModelCompleteButton,
  ordersModelCompleteButtonMobile,
  ordersModelCompletePending,
  ordersModelCompleteButtonShipping,
} from 'components/Table/Orders/models';
import OrdersTable from 'components/Table/Orders';
import { Visibility } from 'components/Visibility';

import IconDown from 'img/arrow-down.svg';
import IconArrowUp from 'img/arrow-up.svg';

import { ITabLabel, OrdersTabsEnum, TabLabelsEnum } from 'models/Order';
import { EnumUserType } from 'models/User';
import { isMobileDevice } from 'utils/ScreenUtils';

import { Button } from 'components/Button';


const Orders: React.FC = () => {
  const user = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();

  const [tabFilter, setTabFilter] = useState();
  const [, setCurrentPage] = useState(0);
  const [tabLabels, setTabLabels] = useState<ITabLabel[]>([]);
  const [isFilterVisible, setIsFilterVisible] = useState(true);
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);

  const pageSize = 10;
  const isMobile = isMobileDevice();

  const fullTabLabels = [
    {
      label: TabLabelsEnum.PEDIDOS_FATURADOS,
      trackingName: 'Aba_Pedidos_Faturados'
    },
    {
      label: TabLabelsEnum.PEDIDOS_PENDENTES,
      trackingName: 'Aba_Pedidos_Pendentes'
    },
    {
      label: TabLabelsEnum.VENDA_TRIANGULAR,
      trackingName: 'Aba_Venda_Triangular'
    }
  ];


  function getModelForGrid() {
    if (isMobile) {
      return ordersModelCompleteButtonMobile();
    }

    return ordersModelCompleteButton(user);
  }

  function getViewFilter() {
    let show = user?.userType !== EnumUserType.SUPPLIER;

    return {
      showSituationFilter: true,
      showEnterpriseFilter: show,
      showSubsidiaryFilter: show
    };
  }

  useEffect(() => {
    const { userType } = user || {};

    if (isMobile) {
      setTabLabels([]);
    } else {
      const filteredTabLabels = userType === EnumUserType.SUPPLIER
        ? fullTabLabels.filter(tabLabel => tabLabel.label === TabLabelsEnum.PEDIDOS_FATURADOS)
        : fullTabLabels;

      setTabLabels(filteredTabLabels);
    }
  }, [user]);

  useEffect(() => {
    return () => {
      dispatch({
        type: 'SET_VIEW_OPTIONS_DOWNLOAD_IN_LOT',
        value: false,
      });
    };
  }, []);

  const titlePage = isMobile ? "Localize o pedido" : "Pedidos";

  const expandedDropdown = () => {
    setIsFilterVisible(!isFilterVisible);
    setIsDropdownExpanded(!isDropdownExpanded);
  };

  return (
    <>

      <Row className='align-md-center justify-md-between'>
        <Text size={24} className="color-primary font-bold">
          {titlePage}
        </Text>

        <Visibility visible={isMobile}>
          <Button
            onClick={expandedDropdown}
            className="no-style"
          >
            <>
              <img
                src={isDropdownExpanded ? IconArrowUp : IconDown}
                alt="toggle"
                style={{
                  transition: 'all .3s ease',
                  transform: isDropdownExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </>
          </Button>
        </Visibility>
      </Row>

      <Visibility visible={!isMobile}>
        <Column className="mt-3">
          <TabNavigation
            trackingKey="Abas Pedidos"
            trackingEvent="Pedidos"
            setCurrentPage={setCurrentPage}
            setTabFilter={(filter) => setTabFilter(filter)}
            tabLabels={tabLabels}
            pages={[
              <Column className="w-100 max-w-lg-100 flex-1">
                <OrdersTable
                  trackingKey="Grid Pedidos Faturados"
                  searchInputPlaceholder="Pedido, nota fiscal, lote, ordem ou produto"
                  showFilter
                  tabFilter={tabFilter}
                  DropdownTemplate={(data: any) => {
                    return (
                      <OrderDropdown
                        {...data}
                        order={data}
                        showDevolutionDownloadButtons
                      />
                    );
                  }}
                  model={getModelForGrid()}
                  pageSize={pageSize}
                  showAdvancedFilters
                  viewFilters={getViewFilter()}
                  showTypeDateFilter
                  showDownloadInLotButton
                  showExportButton={true}
                  clearBtn
                  currentTabId={{
                    name: OrdersTabsEnum.PEDIDO_FATURADO,
                    order: 0
                  }}
                />
              </Column>,
              <Column className="w-100 max-w-lg-100 flex-1">
                <OrdersTable
                  trackingKey="Grid Pedidos Pendentes"
                  searchInputPlaceholder="Pedido, lote, ordem ou produto"
                  showFilter
                  DropdownTemplate={(data: any) => (
                    <OrderDropdown {...data} order={data} />
                  )}
                  tabFilter={tabFilter}
                  model={ordersModelCompletePending}
                  pageSize={pageSize}
                  customProps={{ showOrderDownloadButtons: false }}
                  viewFilters={{
                    showSituationFilter: true
                  }}
                  showExportButton={user?.userType === EnumUserType.EMPLOYEE}
                  currentTabId={{
                    name: OrdersTabsEnum.PEDIDO_PENDENTE,
                    order: 1
                  }}
                />
              </Column>,
              <Column className="w-100 max-w-lg-100 flex-1">
                <OrdersTable
                  trackingKey="Grid Venda Triangular"
                  searchInputPlaceholder="Nº nota de remessa, nome paciente, ordem, produto"
                  showFilter
                  tabFilter={tabFilter}
                  DropdownTemplate={(data: any) => (
                    <OrderDropdown {...data} order={data} />
                  )}
                  model={ordersModelCompleteButtonShipping}
                  pageSize={pageSize}
                  showAdvancedFilters
                  viewFilters={{
                    showSituationFilter: true,
                    showEnterpriseFilter: true,
                    showSubsidiaryFilter: true,
                  }}
                  showTypeDateFilter
                  customProps={{ showOrderDownloadButtons: false, showOrderDownloadShippingButtons: true }}
                  showExportButton
                  showDownloadInLotButton
                  clearBtn
                  currentTabId={{
                    name: OrdersTabsEnum.VENDA_TRIANGULAR,
                    order: 2
                  }}
                />
              </Column>
            ]}
          />
        </Column>
      </Visibility>

      <Visibility visible={isMobile}>
        <Column className="mt-3">
          <TabNavigation
            trackingKey="Abas Pedidos"
            trackingEvent="Pedidos"
            setCurrentPage={setCurrentPage}
            setTabFilter={(filter) => setTabFilter(filter)}
            tabLabels={tabLabels}
            pages={[
              <Column className="w-100 max-w-lg-100 flex-1">
                <OrdersTable
                  trackingKey="Grid Pedidos Faturados"
                  searchInputPlaceholder="Pedido, nota fiscal, lote, ordem ou produto"
                  showFilter={isFilterVisible}
                  tabFilter={tabFilter}
                  tableSearchClassName="grid-gap-2 px-3 py-3"
                  DropdownTemplate={(data: any) => {
                    return (
                      <OrderDropdown
                        {...data}
                        order={data}
                        showDevolutionDownloadButtons
                      />
                    );
                  }}
                  model={getModelForGrid()}
                  pageSize={pageSize}
                  showAdvancedFilters
                  viewFilters={getViewFilter()}
                  showTypeDateFilter
                  showDownloadInLotButton={false}
                  showExportButton={false}
                  clearBtn
                  currentTabId={{
                    name: OrdersTabsEnum.PEDIDO_FATURADO,
                    order: 0
                  }}
                />
              </Column>
            ]}
          />
        </Column>
      </Visibility>
    </>
  );
};

export default Orders;